import * as React from 'react';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { apiStatuses, fetchCustomerServiceInfo } from '../actions/Actions';
import { IProductInfo, IUserInfo } from '../CustomTypes';
import { trackPulsePageViewEvent } from '../PulseUtils';
import {
  classNames,
  FontSize,
  FontWeight,
  Heading,
  HeadingLevel,
  Link,
  Margin,
  MaxWidth,
  Padding,
} from '@snoam/pinata';
import { ClientShortHandName, getMyPageUrl } from '@snoam/mono-scc';
import { isProduction } from '../fastflowUtils';

export interface IStateProps {
  // tslint:disable:no-any
  customerServiceInfo?: any;
  productInfo?: IProductInfo;
  error?: boolean;
  errorJson?: string;
  errorStateName?: string;
  errorSummary?: string;
  message?: string;
  requestUuid?: string;
  requestUrl?: string;
  response?: string;
  status?: number;
  userInfo?: IUserInfo;
}

export interface IErrorMessageDispatchProps {
  fetchCustomerServiceInfo?: () => (dispatch: Dispatch<Action>) => any;
}

export interface IErrorMessageProps
  extends IErrorMessageDispatchProps,
    IStateProps {
  productId: number;
  clientShortHandName: string;
}

const styleClass = {
  image: classNames(MaxWidth.MAX_W_64, Margin.MX_AUTO),
  mainHeading: classNames(
    Margin.MT_6,
    Margin.MB_2,
    FontSize.TEXT_2XL,
    FontWeight.FONT_BOLD,
  ),
  subHeading: classNames(Margin.MT_6, Margin.MB_2),
};

export class ErrorMessage extends React.Component<
  IErrorMessageProps,
  Record<string, never>
> {
  public static displayName = 'ErrorMessage';

  constructor(props: IErrorMessageProps) {
    super(props);
  }

  public componentDidMount() {
    this.props.fetchCustomerServiceInfo();
  }

  public render() {
    const {
      error,
      errorJson,
      errorSummary,
      message,
      status,
      clientShortHandName,
    } = this.props;

    if (error) {
      const eventName = 'Error message';
      trackPulsePageViewEvent(this.props.userInfo, eventName);
    }

    let body: any = message || (
      <>
        <p className={Margin.MB_2}>
          Vi klarte ikke å behandle din forespørsel.
        </p>
      </>
    );

    let header: string = errorSummary || 'Noe gikk galt';

    if (
      status &&
      status === 404 &&
      errorJson &&
      JSON.stringify(errorJson).indexOf('SUBSCRIPTION_TYPE_NOT_ACTIVE') > -1
    ) {
      header = 'Produktet er ikke lenger aktivt';
      body = <p>Vennligst gå tilbake og velg et annet produkt</p>;
    }

    const myPageUrl = getMyPageUrl(
      clientShortHandName as ClientShortHandName,
      'abo',
      isProduction(),
    );

    return error ? (
      <>
        <img
          className={styleClass.image}
          src="https://bestilling.aftenposten.no/s/ff/img/logos/feilmelding_ill.svg"
        />
        <Heading level={HeadingLevel.ONE} className={styleClass.mainHeading}>
          {header}
        </Heading>
        {body}
        <div className={classNames(Padding.PT_4)}>
          <p>
            Ta kontakt med{' '}
            <Link href={`https://${myPageUrl}/kundeservice`}>
              kundesenteret
            </Link>{' '}
            hvis du trenger hjelp.
          </p>
        </div>
      </>
    ) : null;
  }
}

// tslint:disable:no-any
const mapStateToProps = (state: any) => {
  const errorStateName = Object.keys(state)
    .filter(
      (k) =>
        [
          'paymentMethodsInfo',
          'sessionInfo',
          'siebelInfo',
          'findUserByEmail',
          'fetchUserInfo',
          'checkCode',
          'findUserByPhoneNumber',
          'checkDuplicateAndQuarantine',
          'addFamilySharing',
          'cancelPayment',
        ].indexOf(k) === -1,
    )
    .find(
      (k) => state[k].apiStatus && state[k].apiStatus === apiStatuses.ERROR,
    );
  const errorData =
    (errorStateName && state[errorStateName].data) ||
    JSON.stringify(state[errorStateName], null, 2);
  const response =
    (errorStateName && state[errorStateName].response) ||
    JSON.stringify(state[errorStateName], null, 2);
  const requestUuid =
    (errorStateName && state[errorStateName].requestUuid) || '';
  const requestUrl = (errorStateName && state[errorStateName].requestUrl) || '';
  const status = errorStateName && state[errorStateName].status;
  let errorJson: {
    summary?: string;
    message?: string;
  } = {};
  if (errorData) {
    try {
      errorJson = errorData && JSON.parse(errorData);
    } catch (e) {
      // ignored...
    }
  }

  return {
    customerServiceInfo: state.customerServiceInfo,
    error: !!response,
    errorJson,
    errorStateName,
    errorSummary: errorJson.summary || '',
    message: errorJson.message,
    productInfo: state.productInfo,
    requestUrl,
    requestUuid,
    response,
    status,
    userInfo: state.userInfo,
  } as IStateProps;
};

// tslint:disable:no-any
const mapDispatchToProps = (dispatch: any) =>
  bindActionCreators(
    {
      fetchCustomerServiceInfo: () => fetchCustomerServiceInfo(),
    },
    dispatch,
  ) as IErrorMessageDispatchProps;

export default connect(mapStateToProps, mapDispatchToProps)(ErrorMessage);
